import {
  fmtDateInput,
  nyDateInputToMs,
  nyStartOfDayMs,
} from '@/lib/datetime';
import type { Instant } from '@/lib/datetime';

export const CAMPAIGNS_OVERVIEW_CHANNELS = ['meta', 'ctv', 'dsp'] as const;
/** In the Status menu's order (`CAMPAIGN_STATUS_OPTIONS` in dashboard.tsx): Ended third. */
export const CAMPAIGNS_OVERVIEW_STATUSES = [
  'live',
  'ended',
  'paused',
  'scheduled',
  'needs_attention',
  'unverified',
  'paused_by_system',
  'pausing',
  'starting',
  'partially_active',
  'armed',
  'draft',
  'ready_to_publish',
] as const;
export const CAMPAIGNS_OVERVIEW_AUDIENCE_TYPES = [
  'direct',
  'lookalike',
  'retargeting',
] as const;
export const CAMPAIGNS_OVERVIEW_SORT_FIELDS = [
  'needs_attention',
  'campaign',
  'production',
  'channel',
  'status',
  'audience_type',
  'start_date',
  'end_date',
  'budget',
  'gross_spend',
  'pacing',
  'roas',
  'cpa',
  'tickets',
  'ticket_value',
  'impressions',
  'cpm',
] as const;
export const CAMPAIGNS_OVERVIEW_SORT_DIRECTIONS = ['asc', 'desc'] as const;

export type CampaignsOverviewChannel = typeof CAMPAIGNS_OVERVIEW_CHANNELS[number];
export type CampaignsOverviewStatus = typeof CAMPAIGNS_OVERVIEW_STATUSES[number];
export type CampaignsOverviewAudienceType =
  typeof CAMPAIGNS_OVERVIEW_AUDIENCE_TYPES[number];
export type CampaignsOverviewSortField =
  typeof CAMPAIGNS_OVERVIEW_SORT_FIELDS[number];
export type CampaignsOverviewSortDirection =
  typeof CAMPAIGNS_OVERVIEW_SORT_DIRECTIONS[number];

export interface CampaignsOverviewSearch {
  /** Inclusive, literal America/New_York calendar days. */
  from: string;
  to: string;
  /**
   * The URL carried no usable range, so `from`/`to` are the last 30 completed
   * New York days as of the parse and must be re-derived at request time. A
   * parse happens on navigation; a tab left open across ET midnight kept
   * asking for a window that ended two days back, and the server, quite
   * rightly, had nothing for the day it was missing. Never written to the
   * URL and never read from it: only the dates decide.
   */
  rangeIsDefault?: true;
  campaignId?: string;
  channel?: CampaignsOverviewChannel;
  status?: CampaignsOverviewStatus;
  productionId?: string;
  audienceTypes: CampaignsOverviewAudienceType[];
  sortField: CampaignsOverviewSortField;
  sortDirection: CampaignsOverviewSortDirection;
}

export type CampaignsOverviewSearchPatch = Partial<CampaignsOverviewSearch>;

/** What a navigation may put in the URL: anything absent is re-derived. */
export type CampaignsOverviewSearchInput = Partial<CampaignsOverviewSearch>;

export const DEFAULT_CAMPAIGNS_OVERVIEW_SORT_FIELD = 'needs_attention' as const;
export const DEFAULT_CAMPAIGNS_OVERVIEW_SORT_DIRECTION = 'asc' as const;

const DAY_MS = 86_400_000;
const HALF_DAY_MS = DAY_MS / 2;
const DEFAULT_COMPLETED_DAYS = 30;
const MAX_RANGE_DAYS = 366;
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const PRODUCTION_ID = new RegExp(`^(?:${UUID}|Production/${UUID})$`);
const CAMPAIGN_ID = /^[a-z0-9_-]{1,64}$/;

interface DateRange {
  from: string;
  to: string;
}

/** Last 30 fully completed New York days. Today is never selected implicitly. */
export function defaultCampaignsOverviewDateRange(
  now: Instant = Date.now(),
): DateRange {
  const todayStart = nyStartOfDayMs(now);
  let fromStart = todayStart;
  for (let day = 0; day < DEFAULT_COMPLETED_DAYS; day += 1) {
    fromStart = nyStartOfDayMs(fromStart - HALF_DAY_MS);
  }
  return {
    from: fmtDateInput(fromStart),
    to: fmtDateInput(todayStart - 1),
  };
}

export function parseCampaignsOverviewSearch(
  search: object,
  now: Instant = Date.now(),
): CampaignsOverviewSearch {
  const raw = search as Record<string, unknown>;
  const range = parseDateRange(raw.from, raw.to)
    ?? { ...defaultCampaignsOverviewDateRange(now), rangeIsDefault: true as const };
  return {
    ...range,
    campaignId: parseCampaignId(raw.campaignId ?? raw.campaign),
    channel: parseOptionalEnum(raw.channel, CAMPAIGNS_OVERVIEW_CHANNELS),
    status: parseOptionalEnum(raw.status, CAMPAIGNS_OVERVIEW_STATUSES),
    productionId: parseProductionId(raw.productionId ?? raw.production),
    audienceTypes: parseAudienceTypes(
      raw.audienceTypes ?? raw.audience_types ?? raw.audienceType,
    ),
    sortField: parseEnum(
      raw.sortField ?? raw.sort,
      CAMPAIGNS_OVERVIEW_SORT_FIELDS,
      DEFAULT_CAMPAIGNS_OVERVIEW_SORT_FIELD,
    ),
    sortDirection: parseEnum(
      raw.sortDirection ?? raw.direction,
      CAMPAIGNS_OVERVIEW_SORT_DIRECTIONS,
      DEFAULT_CAMPAIGNS_OVERVIEW_SORT_DIRECTION,
    ),
  };
}

/**
 * The URL form of a search state.
 *
 * Behavioural defaults (sort, an empty audience filter) are written so a
 * copied URL stays stable. The one default that is not written is the range:
 * a written date is a pinned date, and "the last 30 completed days" has to
 * keep meaning that after the New York day rolls over.
 */
export function serializeCampaignsOverviewSearch(
  search: CampaignsOverviewSearch,
): CampaignsOverviewSearchInput {
  return withoutDefaultedRange({ ...search, audienceTypes: [...search.audienceTypes] });
}

/**
 * Drop a defaulted range from a search about to be written into the URL.
 *
 * The router writes validated search back on every navigation, defaults
 * included, so without this the first visit would pin `from`/`to` for the
 * life of the tab and every later parse would read them as a choice.
 */
export function withoutDefaultedRange(
  search: CampaignsOverviewSearchInput,
): CampaignsOverviewSearchInput {
  if (!search.rangeIsDefault) return search;
  const stripped = { ...search };
  delete stripped.from;
  delete stripped.to;
  delete stripped.rangeIsDefault;
  return stripped;
}

/**
 * The search a mounted screen renders and requests: a defaulted range
 * re-derived for `now`, so the window it shows and asks for is the last 30
 * completed New York days as of this render rather than as of the last
 * navigation. An explicit range passes through untouched, identity included,
 * so memoised consumers do not churn on it.
 */
export function resolveCampaignsOverviewSearch(
  search: CampaignsOverviewSearch,
  now: Instant = Date.now(),
): CampaignsOverviewSearch {
  if (!search.rangeIsDefault) return search;
  const resolved = { ...search, ...defaultCampaignsOverviewDateRange(now) };
  delete resolved.rangeIsDefault;
  return resolved;
}

export function updateCampaignsOverviewSearch(
  current: CampaignsOverviewSearch,
  patch: CampaignsOverviewSearchPatch,
): CampaignsOverviewSearch {
  const candidate = { ...current, ...patch };
  // A patch that names a bound is a date choice and pins the range; any
  // other patch (a filter, a sort) leaves the range exactly as it was,
  // defaulted and floating included. A defaulted flag in a patch is ignored:
  // only the dates decide, here as in the URL.
  const chosen = patch.from !== undefined || patch.to !== undefined
    ? parseDateRange(candidate.from, candidate.to)
    : undefined;
  const range = chosen ?? {
    from: current.from,
    to: current.to,
    ...(current.rangeIsDefault ? { rangeIsDefault: true as const } : {}),
  };
  return {
    ...range,
    campaignId: parseCampaignId(candidate.campaignId),
    channel: parseOptionalEnum(candidate.channel, CAMPAIGNS_OVERVIEW_CHANNELS),
    status: parseOptionalEnum(candidate.status, CAMPAIGNS_OVERVIEW_STATUSES),
    productionId: parseProductionId(candidate.productionId),
    audienceTypes: parseAudienceTypes(candidate.audienceTypes),
    sortField: parseEnum(
      candidate.sortField,
      CAMPAIGNS_OVERVIEW_SORT_FIELDS,
      current.sortField,
    ),
    sortDirection: parseEnum(
      candidate.sortDirection,
      CAMPAIGNS_OVERVIEW_SORT_DIRECTIONS,
      current.sortDirection,
    ),
  };
}

function parseDateRange(from: unknown, to: unknown): DateRange | undefined {
  const parsedFrom = parseCalendarDay(from);
  const parsedTo = parseCalendarDay(to);
  if (!parsedFrom || !parsedTo || parsedFrom.ms > parsedTo.ms) return undefined;
  const inclusiveDays = Math.round((parsedTo.ms - parsedFrom.ms) / DAY_MS) + 1;
  return inclusiveDays <= MAX_RANGE_DAYS
    ? { from: parsedFrom.value, to: parsedTo.value }
    : undefined;
}

function parseCalendarDay(value: unknown): { value: string; ms: number } | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  const ms = nyDateInputToMs(normalized);
  return Number.isFinite(ms) && fmtDateInput(ms) === normalized
    ? { value: normalized, ms }
    : undefined;
}

function parseProductionId(value: unknown): string | undefined {
  return typeof value === 'string' && PRODUCTION_ID.test(value)
    ? value
    : undefined;
}

function parseCampaignId(value: unknown): string | undefined {
  return typeof value === 'string' && CAMPAIGN_ID.test(value)
    ? value
    : undefined;
}

function parseAudienceTypes(value: unknown): CampaignsOverviewAudienceType[] {
  const candidates = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const selected = new Set(candidates.filter(
    (candidate): candidate is CampaignsOverviewAudienceType =>
      typeof candidate === 'string'
      && CAMPAIGNS_OVERVIEW_AUDIENCE_TYPES.includes(
        candidate as CampaignsOverviewAudienceType,
      ),
  ));
  return CAMPAIGNS_OVERVIEW_AUDIENCE_TYPES.filter((value) => selected.has(value));
}

function parseEnum<const T extends readonly string[]>(
  value: unknown,
  values: T,
  fallback: T[number],
): T[number] {
  return typeof value === 'string' && values.includes(value)
    ? value as T[number]
    : fallback;
}

function parseOptionalEnum<const T extends readonly string[]>(
  value: unknown,
  values: T,
): T[number] | undefined {
  return typeof value === 'string' && values.includes(value)
    ? value as T[number]
    : undefined;
}
